import { isOffsetDateTime, isUuid } from '@/lib/validation';
import type { ServiceClient } from '../http/client';
import { ApiError } from '../http/errors';
import type { DevicePurpose } from './device-purpose';

export interface NativeAccessInventory {
  authority: 'CORE';
  scope: 'CORE_CURRENT';
  property_id: string;
  state: 'CURRENT';
  observed_at: string;
  core_slots: {
    slot_id: string;
    room_id: string;
    name: string | null;
    room_name: string | null;
    slot_version: number;
    room_version: number;
    topology_revision: number;
  }[];
  items: {
    slot_id: string;
    room_id: string;
    state: 'CURRENT' | 'NO_MAPPING' | 'UNCERTAIN' | 'UNCONFIRMED';
    device: {
      /** Stored purpose, absent on older servers. */
      purpose?: DevicePurpose;
      device_id: string;
      gateway_id: string;
      device_type: string | null;
      is_active: boolean;
      provider_support: 'UNVERIFIED';
      core_device_id: string | null;
      revision: number | null;
      authority: 'CORE' | null;
    } | null;
  }[];
  next_cursor: string | null;
}
const record = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === 'object' && !Array.isArray(v);
const nullableText = (v: unknown) => v === null || typeof v === 'string';
const positive = (v: unknown) => Number.isSafeInteger(v) && Number(v) > 0;
const invalid = () =>
  new ApiError({
    kind: 'invalid-response',
    service: 'core',
    message: '현재 출입 장비 응답을 확인하지 못했습니다.',
  });
/** CORE_CURRENT contract at backend 21ac197; no legacy source identity is required. */
export async function readNativeAccessInventory(
  client: ServiceClient,
  propertyId: string,
  cursor: string | null,
  signal?: AbortSignal,
): Promise<NativeAccessInventory> {
  if (!isUuid(propertyId) || (cursor !== null && !cursor)) throw invalid();
  const v = await client.get<unknown>(`admin/properties/${propertyId}/access-devices`, {
    params: { limit: 50, cursor: cursor ?? undefined },
    signal,
  });
  if (
    !record(v) ||
    v.authority !== 'CORE' ||
    v.scope !== 'CORE_CURRENT' ||
    v.state !== 'CURRENT' ||
    typeof v.property_id !== 'string' ||
    v.property_id.toLowerCase() !== propertyId.toLowerCase() ||
    !isOffsetDateTime(v.observed_at) ||
    !(v.next_cursor === null || (typeof v.next_cursor === 'string' && v.next_cursor.length > 0)) ||
    !Array.isArray(v.core_slots) ||
    v.core_slots.length > 50 ||
    !Array.isArray(v.items)
  )
    throw invalid();
  const slots = new Map<string, string>();
  for (const slot of v.core_slots) {
    if (
      !record(slot) ||
      !isUuid(slot.slot_id) ||
      !isUuid(slot.room_id) ||
      !nullableText(slot.name) ||
      !nullableText(slot.room_name) ||
      !positive(slot.slot_version) ||
      !positive(slot.room_version) ||
      !positive(slot.topology_revision) ||
      slots.has(slot.slot_id)
    )
      throw invalid();
    slots.set(slot.slot_id, slot.room_id);
  }
  const seen = new Set<string>();
  for (const item of v.items) {
    if (
      !record(item) ||
      !isUuid(item.slot_id) ||
      seen.has(item.slot_id) ||
      slots.get(item.slot_id) !== item.room_id ||
      !['CURRENT', 'NO_MAPPING', 'UNCERTAIN', 'UNCONFIRMED'].includes(String(item.state))
    )
      throw invalid();
    seen.add(item.slot_id);
    const d = item.device;
    if (
      d !== null &&
      (!record(d) ||
        Object.keys(d).some(
          (key) =>
            ![
              'device_id',
              'gateway_id',
              'device_type',
              'is_active',
              'provider_support',
              'core_device_id',
              'revision',
              'authority',
              'purpose',
            ].includes(key),
        ) ||
        (Object.hasOwn(d, 'purpose') &&
          d.purpose !== 'UNKNOWN' &&
          d.purpose !== 'COMMON_ENTRANCE' &&
          d.purpose !== 'ROOM_LOCK') ||
        typeof d.device_id !== 'string' ||
        typeof d.gateway_id !== 'string' ||
        !nullableText(d.device_type) ||
        typeof d.is_active !== 'boolean' ||
        d.provider_support !== 'UNVERIFIED' ||
        !(d.core_device_id === null || isUuid(d.core_device_id)) ||
        !(d.revision === null || positive(d.revision)) ||
        !(d.authority === null || d.authority === 'CORE'))
    )
      throw invalid();
    if ((item.state === 'CURRENT' || item.state === 'UNCERTAIN') !== (d !== null)) throw invalid();
  }
  if (seen.size !== slots.size) throw invalid();
  return v as unknown as NativeAccessInventory;
}
